import { CONSTANTS } from '../data.js';
import { calculateStats, calculateTotalStats } from './dragonLogic.js';

export const isCompetitorUnlocked = (competitorId, battleProgress) => {
  if (competitorId === 1) return true; // First competitor is always unlocked
  
  // Check if previous competitor has been defeated
  const previousCompetitorIndex = competitorId - 2; // Convert to 0-based index for previous competitor
  return battleProgress[previousCompetitorIndex] >= CONSTANTS.BATTLE_WINS_REQUIRED;
};

export const getCompetitorDifficulty = (competitorId) => {
  if (competitorId <= 5) return { color: '#10B981', text: 'Rookie' };
  if (competitorId <= 10) return { color: '#3B82F6', text: 'Adept' };
  if (competitorId <= 15) return { color: '#F59E0B', text: 'Veteran' };
  if (competitorId <= 19) return { color: '#DC2626', text: 'Master' };
  return { color: '#8B5CF6', text: 'Champion' };
};

export const initializeBattle = (playerTeam, opponent) => {
  const opponentTeam = opponent.deck.map((d, i) => ({
    ...d,
    id: i,
    used: false,
    revealed: false,
  }));

  // Simple shuffle
  for (let i = opponentTeam.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [opponentTeam[i], opponentTeam[j]] = [opponentTeam[j], opponentTeam[i]];
  }

  return {
    phase: 'active',
    round: 1,
    playerScore: 0,
    opponentScore: 0,
    playerTeam: playerTeam.map(d => ({ ...d, used: false })),
    opponentTeam,
    log: [`Battle against ${opponent.name} has begun!`],
    turn: 'player',
    opponentAction: null,
    result: null,
    opponent,
  };
};

export const opponentChooseAction = (opponentTeam) => {
  const availableDragons = opponentTeam.filter(d => !d.used);
  if (availableDragons.length === 0) return null;

  // Simple AI: pick a random available dragon and its best stat
  const chosenDragon = availableDragons[Math.floor(Math.random() * availableDragons.length)];
  const stats = calculateStats(chosenDragon);
  
  const bestStat = Object.entries(stats).reduce((best, current) => {
    return current[1] > best[1] ? current : best;
  }, ['attack', 0]);

  return {
    dragon: chosenDragon,
    stat: bestStat[0],
  };
};

export const resolveRound = (playerDragon, opponentDragon, stat) => {
  const playerStats = calculateStats(playerDragon);
  const opponentStats = calculateStats(opponentDragon);

  const playerStatValue = playerStats[stat];
  const opponentStatValue = opponentStats[stat];

  if (playerStatValue > opponentStatValue) {
    return { winner: 'player', playerStat: playerStatValue, opponentStat: opponentStatValue };
  }
  if (opponentStatValue > playerStatValue) {
    return { winner: 'opponent', playerStat: playerStatValue, opponentStat: opponentStatValue };
  }

  // If stats are tied, check total power
  const playerTotalStats = calculateTotalStats(playerDragon);
  const opponentTotalStats = calculateTotalStats(opponentDragon);

  if (playerTotalStats > opponentTotalStats) {
    return { winner: 'player', playerStat: playerStatValue, opponentStat: opponentStatValue, tieBreaker: 'total_stats', playerTotalStats, opponentTotalStats };
  }
  if (opponentTotalStats > playerTotalStats) {
    return { winner: 'opponent', playerStat: playerStatValue, opponentStat: opponentStatValue, tieBreaker: 'total_stats', playerTotalStats, opponentTotalStats };
  }

  // If total power is also tied, it's a double tie.
  return { winner: 'double_tie', playerStat: playerStatValue, opponentStat: opponentStatValue, playerTotalStats, opponentTotalStats };
};

export const checkBattleEnd = (round, playerScore, opponentScore) => {
  if (round > CONSTANTS.BATTLE_ROUNDS) {
    if (playerScore > opponentScore) return 'victory';
    if (opponentScore > playerScore) return 'defeat';
    return 'tie'; // Fixed: Now properly returns 'tie' instead of 'defeat'
  }

  const remainingRounds = CONSTANTS.BATTLE_ROUNDS - (round - 1);
  if (playerScore > opponentScore + remainingRounds) return 'victory';
  if (opponentScore > playerScore + remainingRounds) return 'defeat';

  return null;
};
